import React, { useState, useEffect, useMemo } from 'react'
import { Typography, Radio, Skeleton, InputNumber } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import axios from '../Utilities/authToken'

const { Title } = Typography

const PRESET_WEIGHTS = [70, 150]

// RTSQ (Polaris) sizes: heading line 20px, each radio row 28px, no gap between rows
const headingStyle = { height: '20px', lineHeight: '20px' }
const radioRowStyle = { height: '28px', display: 'flex', alignItems: 'center' }
const getWeightOption = (w) => (PRESET_WEIGHTS.includes(Number(w)) ? String(w) : 'custom')

const BoxSizesPackagingMethod = () => {
	const [commonPackagingMethod, setCommonPackagingMethod] = useState(1)
	const [perPackageWeight, setPerPackageWeight] = useState(70)
	const [perPackageWeightDistribution, setPerPackageWeightDistribution] = useState('maximize')
	const [customWeightInput, setCustomWeightInput] = useState(null)
	// Which weight option is selected ('70' | '150' | 'custom'), tracked separately
	// from the weight value so "Custom" can be selected even if its value is 70 or 150.
	const [weightOption, setWeightOption] = useState('70')
	const [loading, setLoading] = useState(true)
	const dispatch = useDispatch()
	const { token, installedCarriers } = useSelector(state => state)

	// Get all enabled small carriers - memoized to prevent unnecessary recalculations
	const enabledSmallCarriers = useMemo(() => {
		console.log('🔍 Filtering carriers...')
		console.log('📦 All installedCarriers:', installedCarriers?.map(c => ({
			id: c.id,
			name: c.name,
			slug: c.slug,
			is_enabled: c.is_enabled,
			carrier_type: c.carrier_type
		})))

		const filtered = installedCarriers?.filter(carrier => {
			const isEnabled = carrier.is_enabled === 1
			// Small/Parcel carriers have carrier_type === 2
			const isSmallCarrier = carrier.carrier_type === 2

			console.log(`🔍 Checking carrier ${carrier.name} (${carrier.slug}):`, {
				is_enabled: carrier.is_enabled,
				carrier_type: carrier.carrier_type,
				isEnabled: isEnabled,
				isSmallCarrier: isSmallCarrier,
				qualifies: isEnabled && isSmallCarrier
			})

			return isEnabled && isSmallCarrier
		}) || []

		// Debug logging
		console.log('BoxSizesPackagingMethod Debug:')
		console.log('- installedCarriers count:', installedCarriers?.length)
		console.log('- enabledSmallCarriers count:', filtered.length)
		console.log('- enabledSmallCarriers:', filtered.map(c => ({
			id: c.id,
			name: c.name,
			slug: c.slug,
			carrier_type: c.carrier_type
		})))

		return filtered
	}, [installedCarriers])

	useEffect(() => {
		if (enabledSmallCarriers.length > 0) {
			loadPackagingSettings()
		} else {
			setLoading(false)
		}
	}, [enabledSmallCarriers.length, token])

	// Sets the weight, the custom input, and the selected option together (used on load)
	const applyWeight = (w) => {
		const option = getWeightOption(w)
		setPerPackageWeight(w)
		// Custom box only shows a value when "Custom" is the saved choice
		setCustomWeightInput(option === 'custom' ? w : null)
		setWeightOption(option)
	}

	const applyDefaults = () => {
		setCommonPackagingMethod(1)
		applyWeight(70)
		setPerPackageWeightDistribution('maximize')
	}

	const loadPackagingSettings = async () => {
		try {
			setLoading(true)
			// Load settings from the first enabled small carrier to show current state
			if (enabledSmallCarriers.length > 0) {
				const firstCarrier = enabledSmallCarriers[0]
				const response = await axios.get(`${process.env.REACT_APP_ENITURE_API_URL}/get_qoute_settings/${firstCarrier.id}`, {
					headers: { authorization: `Bearer ${token}` }
				})

				if (response.data && !response.data.error && response.data.data?.value) {
					const parsedData = JSON.parse(response.data.data.value)
					setCommonPackagingMethod(parsedData.packageRatingMethod || 1)
					applyWeight(parsedData.perPackageWeight || 70)
					setPerPackageWeightDistribution(parsedData.perPackageWeightDistribution || 'maximize')
				} else {
					applyDefaults()
				}
			}
		} catch (error) {
			console.error('Error loading packaging settings:', error)
			applyDefaults()
		} finally {
			setLoading(false)
		}
	}

	// Saves packageRatingMethod + perPackageWeight + perPackageWeightDistribution together,
	// since the web service needs all three at once to know how to split the cart weight.
	const savePackagingSettings = async (updates = {}) => {
		try {
			console.log('🔄 savePackagingSettings called with updates:', updates)

			if (enabledSmallCarriers.length === 0) {
				dispatch({
					type: 'ALERT_MESSAGE',
					payload: {
						alertMessage: 'Please enable parcel carrier to use these settings',
						showAlertMessage: true,
						alertMessageType: 'error',
					},
				})

				setTimeout(() => {
					dispatch({
						type: 'ALERT_MESSAGE',
						payload: {
							showAlertMessage: false,
						},
					})
				}, 2000)

				return
			}

			const nextMethod = updates.packageRatingMethod ?? commonPackagingMethod
			const nextWeight = updates.perPackageWeight ?? perPackageWeight
			const nextDistribution = updates.perPackageWeightDistribution ?? perPackageWeightDistribution

			// Update local state immediately for better UX
			setCommonPackagingMethod(nextMethod)
			setPerPackageWeight(nextWeight)
			setPerPackageWeightDistribution(nextDistribution)

			// Update all enabled small carriers
			const updatePromises = enabledSmallCarriers.map(carrier => {
				console.log(`📡 Sending to carrier ${carrier.name} (ID: ${carrier.id})`)
				return axios.post(
					`${process.env.REACT_APP_ENITURE_API_URL}/submit_quote_settings`,
					{
						carrierId: carrier.id,
						packageRatingMethod: nextMethod,
						perPackageWeight: nextWeight,
						perPackageWeightDistribution: nextDistribution,
					},
					{
						headers: { authorization: `Bearer ${token}` }
					}
				)
			})

			console.log('🚀 Created', updatePromises.length, 'promises for carriers')
			const results = await Promise.all(updatePromises)
			console.log('✅ All requests completed:', results.map(r => r.data))

			dispatch({
				type: 'ALERT_MESSAGE',
				payload: {
					alertMessage: 'Packaging method updated for all small carriers',
					showAlertMessage: true,
					alertMessageType: 'success',
				},
			})

			// Hide success message after 2 seconds
			setTimeout(() => {
				dispatch({
					type: 'ALERT_MESSAGE',
					payload: {
						showAlertMessage: false,
					},
				})
			}, 2000)

		} catch (error) {
			console.error('Error updating packaging method:', error)
			// Revert local state on error
			loadPackagingSettings()

			dispatch({
				type: 'ALERT_MESSAGE',
				payload: {
					alertMessage: 'Failed to update packaging method',
					showAlertMessage: true,
					alertMessageType: 'error',
				},
			})
		}
	}

	const MAX_CUSTOM_WEIGHT = 150

	// Shows the "too heavy" toast, then hides it after 2 seconds (same pattern as savePackagingSettings)
	const showMaxWeightError = () => {
		dispatch({
			type: 'ALERT_MESSAGE',
			payload: {
				alertMessage: `Maximum package weight must be less than or equal to ${MAX_CUSTOM_WEIGHT}.`,
				showAlertMessage: true,
				alertMessageType: 'error',
			},
		})
		setTimeout(() => {
			dispatch({ type: 'ALERT_MESSAGE', payload: { showAlertMessage: false } })
		}, 2000)
	}

	// Puts the radio + Custom box back to the last saved weight
	const resetToSavedWeight = () => {
		const option = getWeightOption(perPackageWeight)
		setWeightOption(option)
		setCustomWeightInput(option === 'custom' ? perPackageWeight : null)
	}

	// Select a preset weight (70 / 150): Custom box is cleared
	const handlePresetSelect = (weight) => {
		setWeightOption(String(weight))
		setCustomWeightInput(null)
		if (weight !== perPackageWeight) {
			savePackagingSettings({ perPackageWeight: weight })
		}
	}

	// Select "Custom": box stays empty until the user types a value
	const handleCustomSelect = () => {
		setWeightOption('custom')
		if (customWeightInput > MAX_CUSTOM_WEIGHT) {
			showMaxWeightError()
			return
		}
		if (customWeightInput && customWeightInput !== perPackageWeight) {
			savePackagingSettings({ perPackageWeight: customWeightInput })
		}
	}

	// Save the custom value when the user commits it (blur / Enter)
	const commitCustomWeight = () => {
		if (weightOption !== 'custom') return
		if (!customWeightInput || customWeightInput < 1) {
			// Empty / invalid: go back to the last saved choice
			resetToSavedWeight()
			return
		}
		if (customWeightInput > MAX_CUSTOM_WEIGHT) {
			// Too heavy: show the error only. Custom stays selected with the typed value
			// (same as RTSQ); nothing is saved until the value is fixed.
			showMaxWeightError()
			return
		}
		if (customWeightInput !== perPackageWeight) {
			savePackagingSettings({ perPackageWeight: customWeightInput })
		}
	}

	if (loading) {
		return <Skeleton active />
	}

	return (
		<>
			<label>
				<strong>Package rating method when Standard Box Sizes isn't in use</strong>
			</label>
			<div style={{ marginBottom: '16px', marginTop: '8px' }}>
				<Radio
					checked={commonPackagingMethod === 1}
					onChange={() => savePackagingSettings({ packageRatingMethod: 1 })}
				>
					Quote each item as shipping as its own package
				</Radio>
			</div>
			<div style={{ marginBottom: '8px' }}>
				<Radio
					checked={commonPackagingMethod === 2}
					onChange={() => savePackagingSettings({ packageRatingMethod: 2 })}
				>
					Combine the weight of all items without dimensions and quote them as one package while quoting each item with dimensions as shipping as its own package
				</Radio>
			</div>

			<div style={{ marginBottom: '8px' }}>
				<Radio
					checked={commonPackagingMethod === 3}
					onChange={() => savePackagingSettings({ packageRatingMethod: 3 })}
				>
					Split the combined weight into packages
				</Radio>
			</div>

				{/* Spacing matches RTSQ (Polaris): 20px headings, 28px radio rows, 32px Custom row, 12px between groups */}
				<div style={{ marginLeft: '24px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
					<div>
					<div style={headingStyle}>
						<strong>Maximum package weight</strong>
					</div>
					<div style={radioRowStyle}>
						<Radio
							checked={weightOption === '70'}
							onChange={() => handlePresetSelect(70)}
							disabled={commonPackagingMethod !== 3}
						>
							70 LB
						</Radio>
					</div>
					<div style={radioRowStyle}>
						<Radio
							checked={weightOption === '150'}
							onChange={() => handlePresetSelect(150)}
							disabled={commonPackagingMethod !== 3}
						>
							150 LB
						</Radio>
					</div>
					<div style={{ height: '32px', display: 'flex', alignItems: 'center', gap: '12px' }}>
						<Radio
							checked={weightOption === 'custom'}
							onChange={handleCustomSelect}
							disabled={commonPackagingMethod !== 3}
						>
							Custom
						</Radio>
						<InputNumber
							min={1}
							controls={false}
							style={{ width: '300px', borderRadius: '8px' }}
							value={customWeightInput}
							disabled={weightOption !== 'custom' || commonPackagingMethod !== 3}
							onChange={(value) => setCustomWeightInput(value)}
							onBlur={commitCustomWeight}
							onPressEnter={commitCustomWeight}
						/>
						<span>LB</span>
					</div>
					</div>

					<div>
					<div style={headingStyle}>
						<strong>Weight distribution</strong>
					</div>
					<div style={radioRowStyle}>
						<Radio
							checked={perPackageWeightDistribution === 'equal'}
							onChange={() => savePackagingSettings({ perPackageWeightDistribution: 'equal' })}
							disabled={commonPackagingMethod !== 3}
						>
							Distribute the total weight evenly among packages
						</Radio>
					</div>
					<div style={radioRowStyle}>
						<Radio
							checked={perPackageWeightDistribution === 'maximize'}
							onChange={() => savePackagingSettings({ perPackageWeightDistribution: 'maximize' })}
							disabled={commonPackagingMethod !== 3}
						>
							Fill each package to its maximum before creating the next package
						</Radio>
					</div>
					</div>
				</div>
		</>
	)
}

export default BoxSizesPackagingMethod